import React, { useState, FormEvent, useEffect } from 'react';
import { 
  BarChart2, Image as ImageIcon, Calendar, Sparkles, Key, Clipboard, 
  Plus, Trash2, Edit, X, RefreshCw, Upload, Check, ChevronDown, Database, Home as HomeIcon,
  FolderOpen, Search, Copy, ExternalLink, Film, Play, FileImage, Download, MessageSquare
} from 'lucide-react';
import { Memory, FarmEvent, FarmFeature, HomeSettings, Feedback, PHOTO_FILTERS, getImageStyleFromUrl, getFilterFromUrl, applyFilterToUrl, cleanImageUrl } from '../types';
import { ICON_OPTIONS } from '../data/seedData';

interface AdminPanelProps {
  memories: Memory[];
  events: FarmEvent[];
  features: FarmFeature[];
  homeSettings: HomeSettings;
  onUpdateMemories: (items: Memory[]) => void;
  onUpdateEvents: (events: FarmEvent[]) => void;
  onUpdateFeatures: (features: FarmFeature[]) => void;
  onUpdateHomeSettings: (settings: HomeSettings) => void;
  onResetData: () => void;
  onLogout: () => void;
  onRefreshAllData: () => Promise<void>;
}

export default function AdminPanel({
  memories,
  events,
  features,
  homeSettings,
  onUpdateMemories,
  onUpdateEvents,
  onUpdateFeatures,
  onUpdateHomeSettings,
  onResetData,
  onLogout,
  onRefreshAllData
}: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'homepage' | 'media' | 'events' | 'features' | 'settings' | 'uploads' | 'feedback'>('overview');
  
  // DB status check
  const [dbHealth, setDbHealth] = useState<{ status: string; error?: string; config?: any } | null>(null);

  useEffect(() => {
    fetch('/api/db-status')
      .then(res => res.json())
      .then(data => setDbHealth(data))
      .catch((e) => setDbHealth({ status: 'Disconnected', error: e.message }));
  }, []);
  
  // Notification banner
  const [noti, setNoti] = useState<{ type: 'success' | 'info'; text: string } | null>(null);

  // Modals / Forms Open State
  const [isAlbumModalOpen, setIsAlbumModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);

  // Edit trackers
  const [editingMemoryId, setEditingMemoryId] = useState<string | null>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);

  // FORMS STATE
  // Memory Form
  const [memoryTitle, setMemoryTitle] = useState('');
  const [memoryDate, setMemoryDate] = useState('');
  const [memoryDesc, setMemoryDesc] = useState('');
  const [memoryMediaUrl, setMemoryMediaUrl] = useState('');
  const [memoryMediaType, setMemoryMediaType] = useState<'image' | 'video'>('image');
  const [memoryLikes, setMemoryLikes] = useState(0);
  const [memoryPendingFiles, setMemoryPendingFiles] = useState<File[]>([]);
  const [memoryFilter, setMemoryFilter] = useState('normal');

  // Event Form
  const [eventTitle, setEventTitle] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventDateRaw, setEventDateRaw] = useState('');
  const [eventDesc, setEventDesc] = useState('');
  const [eventLongDesc, setEventLongDesc] = useState('');
  const [eventCategory, setEventCategory] = useState<'upcoming' | 'past'>('upcoming');
  const [eventType, setEventType] = useState('Family');
  const [eventGallery, setEventGallery] = useState(''); // Textarea comma separated
  const [eventPendingFiles, setEventPendingFiles] = useState<File[]>([]);
  const [eventFilter, setEventFilter] = useState('normal');

  // Feature Form
  const [featureName, setFeatureName] = useState('');
  const [featureDesc, setFeatureDesc] = useState('');
  const [featureUrl, setFeatureUrl] = useState('');
  const [featurePendingFiles, setFeaturePendingFiles] = useState<File[]>([]);
  const [featureFilter, setFeatureFilter] = useState('normal');

  // Change Password state
  const [newPass, setNewPass] = useState('');

  // Hero / Home Page editing form state
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [heroFilter, setHeroFilter] = useState('normal');
  const [heroTitle, setHeroTitle] = useState('');
  const [heroSubtitle, setHeroSubtitle] = useState('');
  const [heroButtonText, setHeroButtonText] = useState('');
  const [isHeroSaving, setIsHeroSaving] = useState(false);

  // Sync hero form state when homeSettings prop changes
  useEffect(() => {
    setHeroImageUrl(cleanImageUrl(homeSettings.hero_image_url) || '');
    setHeroFilter(getFilterFromUrl(homeSettings.hero_image_url || ''));
    setHeroTitle(homeSettings.hero_title || '');
    setHeroSubtitle(homeSettings.hero_subtitle || '');
    setHeroButtonText(homeSettings.hero_button_text || '');
  }, [homeSettings]);

  // File Upload state & helper
  const [isUploading, setIsUploading] = useState(false);

  interface UploadedFile {
    filename: string;
    url: string;
    size: number;
    createdAt: string;
    type: 'image' | 'video';
    usages: string[];
  }

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoadingUploads, setIsLoadingUploads] = useState(false);
  const [copiedFileUrl, setCopiedFileUrl] = useState<string | null>(null);

  // Search & Filter state for Uploads Tab
  const [uploadSearch, setUploadSearch] = useState('');
  const [uploadTypeFilter, setUploadTypeFilter] = useState<'all' | 'image' | 'video'>('all');
  const [uploadUsageFilter, setUploadUsageFilter] = useState<'all' | 'used' | 'unused'>('all');

  // Multi-select and Lazy-loading States
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [visibleUploadsCount, setVisibleUploadsCount] = useState(12);

  const handleToggleSelectFile = (filename: string) => {
    setSelectedFiles(prev => 
      prev.includes(filename) ? prev.filter(f => f !== filename) : [...prev, filename]
    );
  };

  const handleSelectAllFiles = (filesToShow: UploadedFile[]) => {
    const allFilenames = filesToShow.map(f => f.filename);
    const areAllSelected = allFilenames.every(f => selectedFiles.includes(f));
    
    if (areAllSelected) {
      setSelectedFiles(prev => prev.filter(f => !allFilenames.includes(f)));
    } else {
      setSelectedFiles(prev => {
        const newSelection = [...prev];
        allFilenames.forEach(f => {
          if (!newSelection.includes(f)) {
            newSelection.push(f);
          }
        });
        return newSelection;
      });
    }
  };

  const handleDeleteSelectedFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    let inUseCount = 0;
    const selectedFilesInfo = uploadedFiles.filter(f => selectedFiles.includes(f.filename));
    selectedFilesInfo.forEach(f => {
      if (f.usages.length > 0) inUseCount++;
    });

    let confirmationMessage = '';
    if (inUseCount > 0) {
      confirmationMessage = `You have selected ${selectedFiles.length} file(s) to delete, of which ${inUseCount} are currently in use. Deleting them will cause broken links/images in the app. Are you sure you want to delete them?`;
    } else {
      confirmationMessage = `Are you sure you want to delete the ${selectedFiles.length} selected file(s)?`;
    }

    if (!window.confirm(confirmationMessage)) return;

    setIsLoadingUploads(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const filename of selectedFiles) {
        const res = await fetch(`/api/admin/uploads/${encodeURIComponent(filename)}`, {
          method: 'DELETE'
        });
        if (res.ok) {
          successCount++;
        } else {
          failCount++;
        }
      }
      
      triggerNoti(`Successfully deleted ${successCount} file(s).` + (failCount > 0 ? ` Failed to delete ${failCount} file(s).` : ''));
      setSelectedFiles([]);
      fetchUploadedFiles();
      await onRefreshAllData();
    } catch (e: any) {
      alert(`Error during bulk delete: ${e.message}`);
    } finally {
      setIsLoadingUploads(false);
    }
  };

  const compressImageIfNeeded = (file: File): Promise<File | Blob> => {
    return new Promise((resolve) => {
      if (file.size <= 1024 * 1024) {
        // Less than 1MB, no compression needed
        resolve(file);
        return;
      }

      console.log(`📉 Compressing image "${file.name}" (original size: ${(file.size / 1024 / 1024).toFixed(2)} MB)...`);

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // If the image is extremely large, scale it down to speed up and reduce size
          const maxDimension = 1920;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file); // Fallback to original
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // We'll iterate on quality from 0.8 down to 0.1 to find a target size under 1MB
          let quality = 0.8;
          const attemptCompression = (q: number) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolve(file); // Fallback
                  return;
                }
                
                if (blob.size <= 1024 * 1024 || q <= 0.1) {
                  console.log(`✅ Compressed image "${file.name}" successfully (final size: ${(blob.size / 1024 / 1024).toFixed(2)} MB, quality: ${q.toFixed(2)})`);
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolve(compressedFile);
                } else {
                  // Try lower quality
                  attemptCompression(q - 0.1);
                }
              },
              'image/jpeg',
              q
            );
          };

          attemptCompression(quality);
        };
        img.onerror = () => resolve(file);
      };
      reader.onerror = () => resolve(file);
    });
  };

  const handleFileUpload = async (files: FileList | null, onComplete: (urls: string[]) => void) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    const uploadedUrls: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        let fileToUpload: File | Blob = file;
        
        if (file.type.startsWith('image/')) {
          fileToUpload = await compressImageIfNeeded(file);
        }
        
        const formData = new FormData();
        formData.append('photos', fileToUpload, file.name);
        
        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }
        
        const data = await response.json();
        if (data.success && data.urls && data.urls.length > 0) {
          uploadedUrls.push(data.urls[0]);
        } else {
          throw new Error(data.error || `Failed to upload ${file.name}`);
        }
      }
      
      onComplete(uploadedUrls);
      triggerNoti(`Successfully uploaded ${files.length} file(s).`);
    } catch (err: any) {
      console.error('Upload error:', err);
      alert('Network error during file upload: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFiles = async (files: File[]): Promise<string[]> => {
    if (files.length === 0) return [];
    setIsUploading(true);
    const uploadedUrls: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        let fileToUpload: File | Blob = file;
        
        if (file.type.startsWith('image/')) {
          fileToUpload = await compressImageIfNeeded(file);
        }
        
        const formData = new FormData();
        formData.append('photos', fileToUpload, file.name);
        
        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }
        
        const data = await response.json();
        if (data.success && data.urls && data.urls.length > 0) {
          uploadedUrls.push(data.urls[0]);
        } else {
          throw new Error(data.error || `Failed to upload ${file.name}`);
        }
      }
      
      triggerNoti(`Successfully uploaded all ${files.length} file(s) to server.`);
      return uploadedUrls;
    } catch (err: any) {
      console.error('Upload error:', err);
      alert('Network error during file upload: ' + err.message);
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  // Preset Unsplash links for easy photo uploads
  const PHOTO_PRESETS = [
    { name: 'Rustic Farmhouse', url: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&q=80&w=800' },
    { name: 'Lush Meadow', url: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&q=80&w=800' },
    { name: 'Warm Gathering Dinner', url: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&q=80&w=800' },
    { name: 'Bonfire Night', url: 'https://images.unsplash.com/photo-1502484063245-56064b162548?auto=format&fit=crop&q=80&w=800' },
    { name: 'Herbs & Sprouts', url: 'https://images.unsplash.com/photo-1530595467537-0b5996c41f2d?auto=format&fit=crop&q=80&w=800' },
    { name: 'Stone Sitting Path', url: 'https://images.unsplash.com/photo-1599599806499-50a501b13dc4?auto=format&fit=crop&q=80&w=800' }
  ];

  const triggerNoti = (text: string, type: 'success' | 'info' = 'success') => {
    setNoti({ type, text });
    setTimeout(() => setNoti(null), 4000);
  };

  const fetchUploadedFiles = async () => {
    setIsLoadingUploads(true);
    try {
      const res = await fetch('/api/admin/uploads');
      if (res.ok) {
        const data = await res.json();
        setUploadedFiles(data);
        setSelectedFiles([]);
        setVisibleUploadsCount(12);
      }
    } catch (e) {
      console.error('Error fetching uploaded files:', e);
    } finally {
      setIsLoadingUploads(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'uploads') {
      fetchUploadedFiles();
    }
  }, [activeTab]);

  const [allFeedbacks, setAllFeedbacks] = useState<Feedback[]>([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState<boolean>(false);

  const fetchAllFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const res = await fetch('/api/feedbacks?all=true');
      const data = await res.json();
      if (Array.isArray(data)) {
        setAllFeedbacks(data);
      }
    } catch (err) {
      console.error('Error fetching feedbacks:', err);
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'feedback') {
      fetchAllFeedbacks();
    }
  }, [activeTab]);

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedFileUrl(url);
    setTimeout(() => setCopiedFileUrl(null), 2000);
  };

  const handleDeleteUploadedFile = async (filename: string, usagesCount: number) => {
    if (usagesCount > 0) {
      const confirmDelete = window.confirm(
        `This file is currently in use in ${usagesCount} place(s). Deleting it will cause broken links/images in the app. Are you sure you want to delete it?`
      );
      if (!confirmDelete) return;
    } else {
      const confirmDelete = window.confirm('Are you sure you want to delete this file?');
      if (!confirmDelete) return;
    }

    try {
      const res = await fetch(`/api/admin/uploads/${encodeURIComponent(filename)}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        triggerNoti('File deleted successfully.');
        fetchUploadedFiles();
        await onRefreshAllData();
      } else {
        const data = await res.json();
        alert(`Failed to delete file: ${data.error || 'Unknown error'}`);
      }
    } catch (e: any) {
      alert(`Error deleting file: ${e.message}`);
    }
  };

  const handleDirectUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      await handleFileUpload(files, () => {
        triggerNoti(`Successfully uploaded and saved ${files.length} asset(s).`);
        fetchUploadedFiles();
      });
    } catch (e: any) {
      alert(`Upload failed: ${e.message}`);
    }
  };

  // -------------------------------------------------------------
  // HANDLERS
  // -------------------------------------------------------------
  
  // Memories - Add / Edit Memory
  const detectMediaType = (url: string): 'image' | 'video' => {
    const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.avi', '.m4v'];
    const lowercaseUrl = url.toLowerCase();
    if (videoExtensions.some(ext => lowercaseUrl.endsWith(ext) || lowercaseUrl.includes(ext + '?') || lowercaseUrl.includes(ext + '&'))) {
      return 'video';
    }
    return 'image';
  };

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSaveMemory = async (e: FormEvent) => {
    e.preventDefault();
    if (!memoryTitle) return;

    const pastedUrls = memoryMediaUrl.trim()
      ? memoryMediaUrl.split(/[\n,]+/).map(url => url.trim()).filter(Boolean)
      : [];

    const hasPendingFiles = memoryPendingFiles.length > 0;
    const hasPastedUrls = pastedUrls.length > 0;

    if (!hasPendingFiles && !hasPastedUrls) {
      alert('Please provide a media URL or select local photo/video to upload.');
      return;
    }

    // Validation
    let isVideo = false;
    if (hasPendingFiles) {
      const videoFiles = memoryPendingFiles.filter(f => f.type.startsWith('video/'));

      if (videoFiles.length > 0) {
        if (memoryPendingFiles.length > 1) {
          alert('You can only upload a single video, and cannot mix video with photos.');
          return;
        }
        isVideo = true;
      }
    } else {
      const videoUrls = pastedUrls.filter(url => detectMediaType(url) === 'video');
      if (videoUrls.length > 0) {
        if (pastedUrls.length > 1) {
          alert('You can only upload a single video, and cannot mix video with photos.');
          return;
        }
        isVideo = true;
      }
    }

    try {
      let finalMediaUrl = '';
      if (hasPendingFiles) {
        const uploaded = await uploadFiles(memoryPendingFiles);
        if (uploaded.length > 0) {
          if (isVideo) {
            finalMediaUrl = uploaded[0];
          } else {
            finalMediaUrl = uploaded.length === 1 ? uploaded[0] : JSON.stringify(uploaded);
          }
        }
      } else {
        if (isVideo) {
          finalMediaUrl = pastedUrls[0];
        } else {
          finalMediaUrl = pastedUrls.length === 1 ? pastedUrls[0] : JSON.stringify(pastedUrls);
        }
      }

      if (!finalMediaUrl) {
        alert('Failed to upload/resolve media URL.');
        return;
      }

      const mediaType: 'image' | 'video' = isVideo ? 'video' : 'image';

      // Apply filter to final media URL
      const finalMediaUrlWithFilter = mediaType === 'image' ? applyFilterToUrl(finalMediaUrl, memoryFilter) : finalMediaUrl;

      if (editingMemoryId) {
        // Edit
        const updated = memories.map(item => {
          if (item.id === editingMemoryId) {
            return {
              ...item,
              title: memoryTitle,
              date: memoryDate || new Date().toISOString().split('T')[0],
              description: memoryDesc,
              mediaUrl: finalMediaUrlWithFilter,
              mediaType,
              likes: memoryLikes
            };
          }
          return item;
        });
        onUpdateMemories(updated);
        triggerNoti('Memory updated successfully.');
      } else {
        // Add new
        const newItem: Memory = {
          id: 'm_' + Date.now(),
          title: memoryTitle,
          date: memoryDate || new Date().toISOString().split('T')[0],
          description: memoryDesc,
          mediaUrl: finalMediaUrlWithFilter,
          mediaType,
          likes: 0,
          createdAt: new Date().toISOString()
        };
        onUpdateMemories([newItem, ...memories]);
        triggerNoti('New memory created successfully.');
      }
      resetMemoryForm();
    } catch (err) {
      console.error(err);
    }
  };

  const startEditMemory = (memory: Memory) => {
    setEditingMemoryId(memory.id);
    setMemoryTitle(memory.title);
    setMemoryDate(memory.date);
    setMemoryDesc(memory.description || '');
    
    let displayUrl = memory.mediaUrl;
    try {
      if (memory.mediaUrl && memory.mediaUrl.trim().startsWith('[')) {
        const parsed = JSON.parse(memory.mediaUrl);
        if (Array.isArray(parsed)) {
          displayUrl = parsed.join(', ');
        }
      }
    } catch (e) {
      console.error(e);
    }
    setMemoryFilter(getFilterFromUrl(memory.mediaUrl));
    setMemoryMediaUrl(displayUrl);
    setMemoryMediaType(memory.mediaType);
    setMemoryLikes(memory.likes);
    setMemoryPendingFiles([]);
    setIsAlbumModalOpen(true);
  };

  const resetMemoryForm = () => {
    setMemoryTitle('');
    setMemoryDate('');
    setMemoryDesc('');
    setMemoryMediaUrl('');
    setMemoryMediaType('image');
    setMemoryLikes(0);
    setMemoryPendingFiles([]);
    setMemoryFilter('normal');
    setEditingMemoryId(null);
    setIsAlbumModalOpen(false);
  };

  // Memories - Delete Memory
  const handleDeleteMemory = (id: string) => {
    if (confirm('Are you sure you want to delete this memory?')) {
      onUpdateMemories(memories.filter(m => m.id !== id));
      triggerNoti('Memory deleted.');
    }
  };

  // Events - Add / Edit
  const handleSaveEvent = async (e: FormEvent) => {
    e.preventDefault();
    if (!eventTitle || !eventDate) return;

    // Split event gallery text into URLs array
    const subGalleries = eventGallery
      ? eventGallery.split(/[\n,]+/).map(url => url.trim()).filter(Boolean)
      : [];

    if (subGalleries.length === 0 && eventPendingFiles.length === 0) {
      alert('Please provide at least one photo URL or select local photos to upload.');
      return;
    }

    try {
      let finalGalleryUrls = [...subGalleries];
      if (eventPendingFiles.length > 0) {
        const uploaded = await uploadFiles(eventPendingFiles);
        finalGalleryUrls = [...finalGalleryUrls, ...uploaded];
      }

      const coverUrl = finalGalleryUrls[0] || '';
      
      // Apply filter to cover and gallery URLs
      const coverUrlWithFilter = applyFilterToUrl(coverUrl, eventFilter);
      const galleryWithFilter = finalGalleryUrls.map(url => applyFilterToUrl(url, eventFilter));

      if (editingEventId) {
        // Edit
        const updated = events.map(ev => {
          if (ev.id === editingEventId) {
            return {
              ...ev,
              title: eventTitle,
              date: eventDate,
              dateRaw: eventDateRaw || new Date().toISOString().split('T')[0],
              description: eventDesc,
              longDescription: eventLongDesc,
              thumbnailUrl: coverUrlWithFilter,
              galleryUrls: galleryWithFilter,
              category: eventCategory,
              type: eventType
            };
          }
          return ev;
        });
        onUpdateEvents(updated);
        triggerNoti('Event log updated successfully.');
      } else {
        // Create new
        const newEv: FarmEvent = {
          id: 'e_' + Date.now(),
          title: eventTitle,
          date: eventDate,
          dateRaw: eventDateRaw || new Date().toISOString().split('T')[0],
          description: eventDesc,
          longDescription: eventLongDesc,
          thumbnailUrl: coverUrlWithFilter,
          galleryUrls: galleryWithFilter,
          category: eventCategory,
          type: eventType
        };
        onUpdateEvents([newEv, ...events]);
        triggerNoti('New event added to family history.');
      }

      resetEventForm();
    } catch (err) {
      console.error(err);
    }
  };

  const startEditEvent = (ev: FarmEvent) => {
    setEditingEventId(ev.id);
    setEventTitle(ev.title);
    setEventDate(ev.date);
    setEventDateRaw(ev.dateRaw);
    setEventDesc(ev.description);
    setEventLongDesc(ev.longDescription || '');
    setEventCategory(ev.category);
    setEventType(ev.type || 'Family');
    setEventGallery(ev.galleryUrls ? ev.galleryUrls.join(', ') : '');
    setEventPendingFiles([]);
    setEventFilter(getFilterFromUrl(ev.thumbnailUrl));
    setIsEventModalOpen(true);
  };

  const resetEventForm = () => {
    setEditingEventId(null);
    setEventTitle('');
    setEventDate('');
    setEventDateRaw('');
    setEventDesc('');
    setEventLongDesc('');
    setEventCategory('upcoming');
    setEventType('Family');
    setEventGallery('');
    setEventPendingFiles([]);
    setEventFilter('normal');
    setIsEventModalOpen(false);
  };

  // Events - Delete Event
  const handleDeleteEvent = (id: string) => {
    if (confirm('Delete this event log? This will remove all associated gallery memories.')) {
      onUpdateEvents(events.filter(e => e.id !== id));
      triggerNoti('Event record deleted.');
    }
  };

  // Features - Add / Edit
  const handleSaveFeature = async (e: FormEvent) => {
    e.preventDefault();
    if (!featureName || !featureDesc) return;

    if (!featureUrl && featurePendingFiles.length === 0) {
      alert('Please provide a photo URL or select a local photo to upload.');
      return;
    }

    try {
      let finalUrl = featureUrl;
      if (featurePendingFiles.length > 0) {
        const uploaded = await uploadFiles(featurePendingFiles);
        if (uploaded[0]) {
          finalUrl = uploaded[0];
        }
      }

      // Apply filter to final feature URL
      const finalUrlWithFilter = applyFilterToUrl(finalUrl, featureFilter);

      if (editingFeatureId) {
        // Edit
        const updated = features.map(feat => {
          if (feat.id === editingFeatureId) {
            return {
              ...feat,
              name: featureName,
              description: featureDesc,
              imageUrl: finalUrlWithFilter
            };
          }
          return feat;
        });
        onUpdateFeatures(updated);
        triggerNoti('Feature record updated.');
      } else {
        // New
        const newFeat: FarmFeature = {
          id: 'f_' + Date.now(),
          name: featureName,
          description: featureDesc,
          imageUrl: finalUrlWithFilter
        };
        onUpdateFeatures([...features, newFeat]);
        triggerNoti('New area feature added.');
      }

      resetFeatureForm();
    } catch (err) {
      console.error(err);
    }
  };

  const startEditFeature = (feat: FarmFeature) => {
    setEditingFeatureId(feat.id);
    setFeatureName(feat.name);
    setFeatureDesc(feat.description);
    setFeatureUrl(feat.imageUrl);
    setFeaturePendingFiles([]);
    setFeatureFilter(getFilterFromUrl(feat.imageUrl));
    setIsFeatureModalOpen(true);
  };

  const resetFeatureForm = () => {
    setEditingFeatureId(null);
    setFeatureName('');
    setFeatureDesc('');
    setFeatureUrl('');
    setFeaturePendingFiles([]);
    setFeatureFilter('normal');
    setIsFeatureModalOpen(false);
  };

  // Features - Delete Feature
  const handleDeleteFeature = (id: string) => {
    if (confirm('Are you sure you want to delete this farm feature?')) {
      onUpdateFeatures(features.filter(f => f.id !== id));
      triggerNoti('Feature deleted successfully.');
    }
  };

  // Settings - Save passphrase
  const handleUpdatePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!newPass || newPass.trim().length < 4) {
      alert('Password must be at least 4 characters long.');
      return;
    }
    try {
      const response = await fetch('/api/admin/change-passcode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPasscode: newPass.trim() })
      });
      const data = await response.json();
      if (data.success) {
        triggerNoti('Admin passcode successfully updated.');
        setNewPass('');
      } else {
        alert('Failed to update passcode: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error updating passcode:', err);
      alert('Network error while updating passcode.');
    }
  };



  return (
    <div id="admin-panel" className="max-w-7xl mx-auto px-4 md:px-8 py-8 min-h-[70vh]">
      
      {/* Title Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-[#2D4A3E]/10 pb-6 mb-8 gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#2C241E] flex items-center gap-2">
            Admin Workspace
          </h1>
          <p className="text-xs text-[#2C241E]/60 mt-1">
            Manage the family farmhouse journal, galleries, logs, and ground features.
          </p>
        </div>
        <button
          onClick={onLogout}
          className="px-5 py-2 border border-[#E25C5C]/35 hover:bg-[#E25C5C]/5 text-[#E25C5C] hover:border-[#E25C5C] text-sm font-medium rounded-full cursor-pointer transition-colors"
        >
          Sign Out of Admin
        </button>
      </div>

      {/* Notification Banner */}
      {noti && (
        <div className={`p-4 mb-6 rounded-xl flex items-center justify-between border select-none animate-fadeIn ${
          noti.type === 'success' 
            ? 'bg-[#E2ECE9] border-[#2D4A3E]/20 text-[#2D4A3E]' 
            : 'bg-[#F4DFD4] border-[#8C5D3A]/20 text-[#8C5D3A]'
        }`}>
          <div className="flex items-center space-x-2 text-xs font-medium">
            <Check size={16} />
            <span>{noti.text}</span>
          </div>
          <button onClick={() => setNoti(null)} className="text-current opacity-60 hover:opacity-100 cursor-pointer">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Primary Split Layout Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        
        {/* Sidebar Navigation */}
        <div id="admin-sidebar" className="flex flex-col space-y-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'overview'
                ? 'bg-[#2D4A3E] text-[#FAF7F2]'
                : 'text-[#2C241E]/80 hover:bg-[#2D4A3E]/5'
            }`}
          >
            <BarChart2 size={16} />
            <span>Overview Dashboard</span>
          </button>

          <button
            onClick={() => setActiveTab('homepage')}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'homepage'
                ? 'bg-[#2D4A3E] text-[#FAF7F2]'
                : 'text-[#2C241E]/80 hover:bg-[#2D4A3E]/5'
            }`}
          >
            <HomeIcon size={16} />
            <span>Home Page</span>
          </button>

          <button
            onClick={() => setActiveTab('media')}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'media'
                ? 'bg-[#2D4A3E] text-[#FAF7F2]'
                : 'text-[#2C241E]/80 hover:bg-[#2D4A3E]/5'
            }`}
          >
            <ImageIcon size={16} />
            <span>Memories Feed ({memories.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('uploads')}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'uploads'
                ? 'bg-[#2D4A3E] text-[#FAF7F2]'
                : 'text-[#2C241E]/80 hover:bg-[#2D4A3E]/5'
            }`}
          >
            <FolderOpen size={16} />
            <span>Storage Assets</span>
          </button>

          <button
            onClick={() => setActiveTab('events')}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'events'
                ? 'bg-[#2D4A3E] text-[#FAF7F2]'
                : 'text-[#2C241E]/80 hover:bg-[#2D4A3E]/5'
            }`}
          >
            <Calendar size={16} />
            <span>Event Logs ({events.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('features')}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'features'
                ? 'bg-[#2D4A3E] text-[#FAF7F2]'
                : 'text-[#2C241E]/80 hover:bg-[#2D4A3E]/5'
            }`}
          >
            <Sparkles size={16} />
            <span>Farm Features ({features.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'settings'
                ? 'bg-[#2D4A3E] text-[#FAF7F2]'
                : 'text-[#2C241E]/80 hover:bg-[#2D4A3E]/5'
            }`}
          >
            <Key size={16} />
            <span>System Settings</span>
          </button>

          <button
            onClick={() => setActiveTab('feedback')}
            className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'feedback'
                ? 'bg-[#2D4A3E] text-[#FAF7F2]'
                : 'text-[#2C241E]/80 hover:bg-[#2D4A3E]/5'
            }`}
          >
            <MessageSquare size={16} />
            <span>Guest Feedback</span>
          </button>
        </div>

        {/* Dynamic Detail Content Pane */}
        <div id="admin-detail-pane" className="md:col-span-3 bg-[#FAF7F2] border border-[#2D4A3E]/10 rounded-2xl p-6 md:p-8 shadow-sm">
          
          {/* ======================= OVERVIEW ======================= */}
          {activeTab === 'overview' && (
            <div className="space-y-8 animate-fadeIn">
              <h2 className="font-serif text-2xl font-semibold text-[#2C241E]">
                Family Dashboard Overview
              </h2>

              {/* Database Integration Diagnostic Banner */}
              {dbHealth && (
                <div className={`p-4 rounded-2xl border text-xs leading-relaxed flex flex-col md:flex-row justify-between items-start md:items-center gap-4 ${
                  dbHealth.status === 'Connected' 
                    ? 'bg-[#E2ECE9] border-[#2D4A3E]/20 text-[#2D4A3E]' 
                    : 'bg-[#FAF4E9] border-[#DDA15E]/30 text-[#8C5D3A]'
                }`}>
                  <div className="flex items-start gap-3">
                    <Database size={18} className={`mt-0.5 flex-shrink-0 ${dbHealth.status === 'Connected' ? 'text-[#2D4A3E]' : 'text-[#AF8D70]'}`} />
                    <div className="space-y-1">
                      <div className="font-bold flex items-center gap-1.5 text-sm">
                        <span>Database Connection:</span>
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                          dbHealth.status === 'Connected' ? 'bg-[#2D4A3E] animate-pulse' : 'bg-[#E25C5C]'
                        }`} />
                        <span className="capitalize">{dbHealth.status}</span>
                      </div>
                      
                      {dbHealth.status === 'Connected' ? (
                        <p className="text-[#2D4A3E]/85">
                          Successfully integrated with your VM's live MySQL server (<strong>{dbHealth.config?.host}:{dbHealth.config?.port}</strong>, database: <strong>{dbHealth.config?.database}</strong>).
                        </p>
                      ) : (
                        <div className="space-y-1 text-[#2C241E]/75">
                          <p>
                            Could not connect to the VM's MySQL server at <strong>{dbHealth.config?.host}:{dbHealth.config?.port}</strong> with user <strong>{dbHealth.config?.user}</strong>.
                          </p>
                          <p className="font-mono text-[11px] bg-black/5 px-2 py-0.5 rounded text-[#E25C5C] max-w-full overflow-x-auto break-all">
                            Reason: {dbHealth.error || 'connect ECONNREFUSED'}
                          </p>
                          <p className="text-[10px] text-[#2C241E]/65 italic">
                            💡 Setup Tip: Make sure your MySQL daemon is running on that port, or update your host configuration in the <code>.env</code> file.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                      dbHealth.status === 'Connected' 
                        ? 'bg-[#2D4A3E] text-white' 
                        : 'bg-[#AF8D70] text-[#FAF7F2]'
                    }`}>
                      {dbHealth.status === 'Connected' ? '✓ Persistent Live Database Sync' : '☕ Seamless Offline Sandbox Mode'}
                    </span>
                  </div>
                </div>
              )}

              {/* Statistics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="p-5 rounded-2xl bg-white border border-[#2D4A3E]/10 flex flex-col justify-between h-32">
                  <span className="text-xs text-[#2C241E]/55 text-uppercase font-medium tracking-wide">
                    MEMORIES FEED
                  </span>
                  <div>
                    <span className="font-serif text-4xl font-bold text-[#2D4A3E]">
                      {memories.length}
                    </span>
                    <span className="text-xs text-[#2C241E]/40 block mt-1">Shared family memories</span>
                  </div>
                </div>

                <div className="p-5 rounded-2xl bg-white border border-[#2D4A3E]/10 flex flex-col justify-between h-32">
                  <span className="text-xs text-[#2C241E]/55 text-uppercase font-medium tracking-wide">
                    PLANS & LOGS
                  </span>
                  <div>
                    <span className="font-serif text-4xl font-bold text-[#8C5D3A]">
                      {events.length}
                    </span>
                    <span className="text-xs text-[#2C241E]/40 block mt-1">Upcoming/Past events</span>
                  </div>
                </div>

                <button
                  onClick={() => setIsAlbumModalOpen(true)}
                  className="p-5 rounded-2xl bg-[#2D4A3E]/5 border border-dashed border-[#2D4A3E]/30 hover:bg-[#2D4A3E]/10 transition-colors flex flex-col items-center justify-center text-center cursor-pointer group h-32"
                >
                  <Plus className="text-[#2D4A3E] group-hover:scale-110 transition-transform mb-2" size={24} />
                  <span className="text-sm font-semibold text-[#2D4A3E] block">
                    Post Memory
                  </span>
                  <span className="text-[10px] text-[#2C241E]/50 block mt-1">Add a photo or video memory</span>
                </button>
              </div>

              {/* Quick Guide / Checklist */}
              <div className="bg-[#FAF7F2] rounded-xl p-5 border border-[#2D4A3E]/10 space-y-4">
                <h3 className="font-serif text-lg font-medium text-[#2C241E] flex items-center gap-2">
                  <Clipboard size={18} className="text-[#2D4A3E]" />
                  <span>Interactive Walkthrough</span>
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-[#2C241E]/80">
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-[#2D4A3E] rounded-full mt-1.5 flex-shrink-0" />
                    <span>Upload or link beautiful photos and videos into your memories feed.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-[#2D4A3E] rounded-full mt-1.5 flex-shrink-0" />
                    <span>Log family suppers or planting tasks in the events timeline scheduler.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-[#AF8D70] rounded-full mt-1.5 flex-shrink-0" />
                    <span>Represent customized ground details like veranda swings, wells, or gardens.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-[#AF8D70] rounded-full mt-1.5 flex-shrink-0" />
                    <span>Change the admin password or restore everything using System Settings.</span>
                  </li>
                </ul>
              </div>

              {/* Recent Active list items */}
              <div className="space-y-4">
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#2C241E]/40 block">
                  Quick Logs Activity list ({events.slice(0, 2).length} events shown)
                </span>
                <div className="space-y-3">
                  {events.slice(0, 2).map((ev) => (
                    <div key={ev.id} className="flex justify-between items-center bg-white border border-[#2D4A3E]/10 p-4 rounded-xl">
                      <div className="flex items-center space-x-4">
                        <img src={ev.thumbnailUrl} className="w-10 h-10 object-cover rounded-lg" />
                        <div>
                          <span className="text-sm font-semibold text-[#2C241E]">{ev.title}</span>
                          <span className="text-[11px] text-[#2C241E]/40 block">{ev.date} • {ev.type}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => startEditEvent(ev)}
                        className="p-2 hover:bg-[#2D4A3E]/5 rounded-lg text-[#2D4A3E] cursor-pointer"
                        title="Edit Log"
                      >
                        <Edit size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ======================= HOME PAGE ======================= */}
          {activeTab === 'homepage' && (
            <div className="space-y-8 animate-fadeIn">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2C241E]">
                  Home Page Editor
                </h2>
                <p className="text-xs text-[#2C241E]/60 mt-1">
                  Customise the hero background image and the welcome text that visitors see first.
                </p>
              </div>

              {/* Live Preview Strip */}
              {heroImageUrl && (
                <div className="relative h-44 rounded-2xl overflow-hidden border border-[#2D4A3E]/15 shadow-sm">
                  {/* Filtered background layer */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ 
                      backgroundImage: `url('${cleanImageUrl(heroImageUrl)}')`,
                      filter: PHOTO_FILTERS.find(f => f.id === heroFilter)?.style
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-[#6B705C]/40 via-black/20 to-black/80" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                    <p className="font-serif text-xl font-bold text-white drop-shadow leading-tight">
                      {heroTitle || 'Welcome to our sanctuary.'}
                    </p>
                    <p className="text-white/80 text-xs mt-2 max-w-sm line-clamp-2 font-sans">
                      {heroSubtitle || 'A place for memories and family gatherings...'}
                    </p>
                    <span className="mt-3 px-4 py-1.5 bg-[#6B705C] text-white text-[10px] font-bold rounded uppercase tracking-widest">
                      {heroButtonText || 'Explore Our Home'}
                    </span>
                  </div>
                  <span className="absolute top-3 right-3 text-[9px] bg-white/20 backdrop-blur-sm text-white px-2 py-0.5 rounded-full font-mono border border-white/30">
                    Live Preview
                  </span>
                </div>
              )}

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setIsHeroSaving(true);
                  await onUpdateHomeSettings({
                    hero_image_url: applyFilterToUrl(heroImageUrl, heroFilter),
                    hero_title: heroTitle,
                    hero_subtitle: heroSubtitle,
                    hero_button_text: heroButtonText
                  });
                  setIsHeroSaving(false);
                  triggerNoti('Home page settings saved successfully.');
                }}
                className="bg-white border border-[#2D4A3E]/10 p-6 rounded-2xl space-y-5 shadow-sm"
              >
                {/* Hero Background Image */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Hero Background Image</label>
                    <label className="text-xs text-[#2D4A3E] hover:underline cursor-pointer flex items-center gap-1 font-semibold">
                      <Upload size={13} />
                      <span>Upload Photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          handleFileUpload(e.target.files, (urls) => {
                            if (urls[0]) setHeroImageUrl(urls[0]);
                          });
                        }}
                      />
                    </label>
                  </div>
                  <input
                    type="text"
                    placeholder="https://... or /uploads/your-photo.jpg"
                    value={heroImageUrl}
                    onChange={(e) => setHeroImageUrl(e.target.value)}
                    className="w-full bg-[#FAF7F2] border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-[#2D4A3E] font-mono"
                  />
                  {isUploading && (
                    <div className="text-[10px] text-[#2D4A3E] font-medium animate-pulse flex items-center gap-1">
                      <RefreshCw size={10} className="animate-spin" />
                      <span>Uploading image, please wait...</span>
                    </div>
                  )}

                  {/* Photo Filter Selector */}
                  {heroImageUrl && (
                    <div className="space-y-1.5 pt-2">
                      {/* Large Background Image Preview */}
                      <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center border border-[#2D4A3E]/10 shadow-inner mb-3">
                        <img
                          src={cleanImageUrl(heroImageUrl)}
                          className="w-full h-full object-cover animate-fadeIn"
                          style={{ filter: PHOTO_FILTERS.find(f => f.id === heroFilter)?.style }}
                        />
                        <span className="absolute bottom-2 left-2 text-[9px] bg-black/60 backdrop-blur-md text-white px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                          Background Image Preview
                        </span>
                      </div>

                      <label className="text-[9px] font-bold text-[#2C241E]/40 uppercase tracking-wide block">Select Hero Image Effect / Filter</label>
                      <div className="flex flex-wrap gap-1.5">
                        {PHOTO_FILTERS.map((filter) => (
                          <button
                            type="button"
                            key={filter.id}
                            onClick={() => setHeroFilter(filter.id)}
                            className={`px-2.5 py-1 rounded-lg border text-[10px] font-bold cursor-pointer transition-all ${
                              heroFilter === filter.id
                                ? 'bg-[#2D4A3E] text-white border-[#2D4A3E]'
                                : 'bg-[#FAF7F2] text-[#2C241E]/75 hover:bg-[#FAF7F2]/70 border-[#2D4A3E]/10'
                            }`}
                          >
                            {filter.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Hero Title */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Hero Title</label>
                  <input
                    type="text"
                    placeholder="Welcome to our sanctuary."
                    value={heroTitle}
                    onChange={(e) => setHeroTitle(e.target.value)}
                    className="w-full bg-[#FAF7F2] border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-[#2D4A3E]"
                  />
                </div>

                {/* Hero Subtitle */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Hero Subtitle / Description</label>
                  <textarea
                    rows={3}
                    placeholder="A place for memories and family gatherings..."
                    value={heroSubtitle}
                    onChange={(e) => setHeroSubtitle(e.target.value)}
                    className="w-full bg-[#FAF7F2] border border-[#2D4A3E]/10 text-xs p-3 rounded-xl focus:outline-none resize-none"
                  />
                </div>

                {/* CTA Button Text */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Button Text</label>
                  <input
                    type="text"
                    placeholder="Explore Our Home"
                    value={heroButtonText}
                    onChange={(e) => setHeroButtonText(e.target.value)}
                    className="w-full bg-[#FAF7F2] border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-[#2D4A3E]"
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={isHeroSaving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-[#2D4A3E] hover:bg-[#1E332A] text-white text-xs font-semibold rounded-xl cursor-pointer transition-colors disabled:opacity-60"
                  >
                    {isHeroSaving ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
                    <span>{isHeroSaving ? 'Saving...' : 'Save Home Page Settings'}</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ======================= MEMORIES FEED ======================= */}
          {activeTab === 'media' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex justify-between items-center border-b border-[#2D4A3E]/10 pb-4">
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-[#2C241E]">
                    Memories Feed
                  </h2>
                  <p className="text-xs text-[#2C241E]/60">
                    Add, edit, or remove photo or video posts in the memories feed.
                  </p>
                </div>
                <button
                  onClick={() => setIsAlbumModalOpen(true)}
                  className="flex items-center space-x-2 bg-[#2D4A3E] hover:bg-[#1E332A] text-white font-medium text-xs px-4 py-2 rounded-xl transition-all cursor-pointer font-mono"
                >
                  <Plus size={14} />
                  <span>Post Memory</span>
                </button>
              </div>

              {/* Memories Admin Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {memories.map(memory => {
                  return (
                    <div key={memory.id} className="bg-white border border-[#2D4A3E]/10 rounded-2xl overflow-hidden shadow-sm flex flex-col justify-between">
                      <div className="relative h-48 bg-black flex items-center justify-center overflow-hidden">
                        {memory.mediaUrl ? (
                          memory.mediaType === 'video' ? (
                            <div className="relative w-full h-full">
                              <video src={memory.mediaUrl} className="w-full h-full object-cover" muted playsInline />
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <Play size={24} className="text-white opacity-80" />
                              </div>
                            </div>
                          ) : (
                            <img 
                              src={(() => {
                                try {
                                  if (memory.mediaUrl && memory.mediaUrl.trim().startsWith('[')) {
                                    const parsed = JSON.parse(memory.mediaUrl);
                                    if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
                                  }
                                } catch (e) {}
                                return memory.mediaUrl;
                              })()} 
                              alt={memory.title} 
                              className="w-full h-full object-cover" 
                            />
                          )
                        ) : (
                          <div className="w-full h-full bg-[#E2ECE9] flex items-center justify-center text-[#2D4A3E] text-xs font-mono">No Media</div>
                        )}
                        <span className="absolute top-2 left-2 bg-[#FAF7F2]/90 backdrop-blur-sm border border-[#2D4A3E]/10 text-[#2D4A3E] text-[9px] font-bold font-mono px-2 py-0.5 rounded-full uppercase">
                          {memory.mediaType}
                        </span>
                        <span className="absolute top-2 right-2 bg-black/60 text-white text-[9px] font-bold font-mono px-2 py-0.5 rounded-full">
                          {memory.date}
                        </span>
                      </div>
                      
                      <div className="p-4 flex-grow flex flex-col justify-between space-y-3">
                        <div>
                          <h3 className="text-sm font-bold text-[#2C241E] leading-tight font-serif">{memory.title}</h3>
                          {memory.description && (
                            <p className="text-xs text-[#2C241E]/70 line-clamp-3 leading-relaxed mt-1 font-sans">
                              {memory.description}
                            </p>
                          )}
                        </div>
                        
                        <div className="border-t border-[#2C241E]/5 pt-3 mt-3 flex justify-between items-center text-[10px] font-mono">
                          <span className="text-[#2C241E]/40 select-all">ID: {memory.id}</span>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => startEditMemory(memory)}
                              className="flex items-center space-x-1 text-[#2D4A3E] hover:text-[#1E332A] p-1 rounded hover:bg-[#2D4A3E]/5 cursor-pointer font-medium"
                            >
                              <Edit size={13} />
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={() => {
                                let url = memory.mediaUrl;
                                try {
                                  if (memory.mediaUrl && memory.mediaUrl.trim().startsWith('[')) {
                                    const parsed = JSON.parse(memory.mediaUrl);
                                    if (Array.isArray(parsed) && parsed.length > 0) url = parsed[0];
                                  }
                                } catch (e) {}
                                const ext = memory.mediaType === 'video' ? '.mp4' : '.jpg';
                                downloadFile(url, `${memory.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}${ext}`);
                              }}
                              className="flex items-center space-x-1 text-[#AF8D70] hover:text-[#8C5D3A] p-1 rounded hover:bg-[#AF8D70]/5 cursor-pointer font-medium"
                              title="Download media"
                            >
                              <Download size={13} />
                              <span>Download</span>
                            </button>
                            <button
                              onClick={() => handleDeleteMemory(memory.id)}
                              className="flex items-center space-x-1 text-[#E25C5C] hover:text-[#c44343] p-1 rounded hover:bg-[#E25C5C]/5 cursor-pointer font-medium"
                            >
                              <Trash2 size={13} />
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ======================= STORAGE ASSETS ======================= */}
          {activeTab === 'uploads' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center border-b border-[#2D4A3E]/10 pb-4 gap-4">
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-[#2C241E]">
                    Storage Assets
                  </h2>
                  <p className="text-xs text-[#2C241E]/60">
                    Manage direct file uploads, view where they are referenced, and clean up orphaned files.
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2 bg-[#2D4A3E] hover:bg-[#1E332A] text-white font-medium text-xs px-4 py-2.5 rounded-xl transition-all cursor-pointer font-mono shadow-sm">
                    <Upload size={14} />
                    <span>Upload Assets</span>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleDirectUpload(e.target.files)}
                    />
                  </label>
                  <button
                    onClick={fetchUploadedFiles}
                    disabled={isLoadingUploads}
                    className="p-2.5 bg-white border border-[#2D4A3E]/10 hover:bg-[#2D4A3E]/5 text-[#2D4A3E] rounded-xl transition-all cursor-pointer disabled:opacity-50"
                    title="Refresh list"
                  >
                    <RefreshCw size={14} className={isLoadingUploads ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Search and Filters Bar */}
              <div className="bg-[#FAF7F2] p-4 border border-[#2D4A3E]/10 rounded-2xl flex flex-col md:flex-row gap-4 justify-between items-center">
                {/* Search */}
                <div className="relative w-full md:w-72">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-[#2C241E]/40">
                    <Search size={14} />
                  </span>
                  <input
                    type="text"
                    placeholder="Search filename or usage..."
                    value={uploadSearch}
                    onChange={(e) => setUploadSearch(e.target.value)}
                    className="w-full bg-white border border-[#2D4A3E]/10 pl-9 pr-3 py-2 rounded-xl text-xs focus:outline-none focus:border-[#2D4A3E] text-[#2C241E]"
                  />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                  {/* Type Filter */}
                  <div className="flex items-center space-x-1">
                    <span className="text-[10px] uppercase font-bold text-[#2C241E]/40 font-mono">Type:</span>
                    <select
                      value={uploadTypeFilter}
                      onChange={(e) => setUploadTypeFilter(e.target.value as any)}
                      className="bg-white border border-[#2D4A3E]/10 rounded-lg text-xs px-2 py-1 focus:outline-none focus:border-[#2D4A3E] text-[#2C241E]"
                    >
                      <option value="all">All</option>
                      <option value="image">Images</option>
                      <option value="video">Videos</option>
                    </select>
                  </div>

                  {/* Usage Filter */}
                  <div className="flex items-center space-x-1">
                    <span className="text-[10px] uppercase font-bold text-[#2C241E]/40 font-mono">Usage:</span>
                    <select
                      value={uploadUsageFilter}
                      onChange={(e) => setUploadUsageFilter(e.target.value as any)}
                      className="bg-white border border-[#2D4A3E]/10 rounded-lg text-xs px-2 py-1 focus:outline-none focus:border-[#2D4A3E] text-[#2C241E]"
                    >
                      <option value="all">All</option>
                      <option value="used">In Use</option>
                      <option value="unused">Unused (Safe to delete)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Uploads Grid */}
              {isLoadingUploads && uploadedFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-[#2D4A3E]/60 space-y-2">
                  <RefreshCw size={24} className="animate-spin" />
                  <p className="text-xs font-mono">Loading storage assets...</p>
                </div>
              ) : (
                (() => {
                  // Filter client-side
                  const filtered = uploadedFiles.filter(file => {
                    // Search term match
                    const matchSearch = file.filename.toLowerCase().includes(uploadSearch.toLowerCase()) ||
                      file.usages.some(u => u.toLowerCase().includes(uploadSearch.toLowerCase()));
                    
                    // Type filter match
                    const matchType = uploadTypeFilter === 'all' || file.type === uploadTypeFilter;

                    // Usage filter match
                    const matchUsage = uploadUsageFilter === 'all' || 
                      (uploadUsageFilter === 'used' && file.usages.length > 0) || 
                      (uploadUsageFilter === 'unused' && file.usages.length === 0);

                    return matchSearch && matchType && matchUsage;
                  });

                  if (filtered.length === 0) {
                    return (
                      <div className="bg-white border border-[#2D4A3E]/10 rounded-2xl py-12 flex flex-col items-center justify-center text-center p-6">
                        <FolderOpen size={36} className="text-[#2D4A3E]/30 mb-2" />
                        <h4 className="text-sm font-bold text-[#2C241E] font-serif">No assets found</h4>
                        <p className="text-xs text-[#2C241E]/60 max-w-xs mt-1">
                          No uploaded items match your filters or search query. Try resetting filters or upload some assets.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {/* Selection & Multi-delete Bar */}
                      <div className="bg-[#FAF7F2] px-4 py-3 border border-[#2D4A3E]/10 rounded-2xl flex flex-row justify-between items-center text-xs animate-fadeIn">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filtered.length > 0 && filtered.every(f => selectedFiles.includes(f.filename))}
                            onChange={() => handleSelectAllFiles(filtered)}
                            className="w-4 h-4 rounded text-[#2D4A3E] focus:ring-[#2D4A3E] border-[#2D4A3E]/30 cursor-pointer"
                          />
                          <span className="font-semibold text-[#2C241E]/80">
                            {selectedFiles.length > 0 
                              ? `${selectedFiles.length} item(s) selected` 
                              : 'Select All Items'}
                          </span>
                        </div>

                        {selectedFiles.length > 0 && (
                          <button
                            type="button"
                            onClick={handleDeleteSelectedFiles}
                            className="flex items-center space-x-1 bg-[#E25C5C] hover:bg-[#c44343] text-white text-[11px] font-bold px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                          >
                            <Trash2 size={12} />
                            <span>Delete Selected</span>
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {filtered.slice(0, visibleUploadsCount).map(file => {
                          const inUse = file.usages.length > 0;
                          const formattedSize = file.size > 1024 * 1024
                            ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                            : `${(file.size / 1024).toFixed(1)} KB`;
                          
                          const dateObj = new Date(file.createdAt);
                          const formattedDate = dateObj.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });

                          return (
                            <div
                              key={file.filename}
                              className={`bg-white border rounded-2xl overflow-hidden shadow-sm flex flex-col justify-between group hover:shadow-md transition-all duration-300 ${
                                selectedFiles.includes(file.filename) ? 'border-[#2D4A3E] ring-2 ring-[#2D4A3E]/20' : 'border-[#2D4A3E]/10'
                              }`}
                            >
                              {/* Preview Area */}
                              <div className="relative h-40 bg-[#FAF7F2] overflow-hidden flex items-center justify-center border-b border-[#2D4A3E]/5">
                                {/* Selection Checkbox Overlay */}
                                <div className="absolute top-2 left-2 z-10">
                                  <input
                                    type="checkbox"
                                    checked={selectedFiles.includes(file.filename)}
                                    onChange={() => handleToggleSelectFile(file.filename)}
                                    className="w-4.5 h-4.5 rounded bg-white/90 backdrop-blur-sm border-[#2D4A3E]/30 text-[#2D4A3E] focus:ring-[#2D4A3E] cursor-pointer shadow-sm"
                                  />
                                </div>

                                {file.type === 'video' ? (
                                  <div className="relative w-full h-full group/vid">
                                    <video
                                      src={file.url}
                                      className="w-full h-full object-cover"
                                      preload="metadata"
                                      muted
                                      loop
                                      playsInline
                                      onMouseOver={(e) => (e.target as HTMLVideoElement).play().catch(() => {})}
                                      onMouseOut={(e) => (e.target as HTMLVideoElement).pause()}
                                    />
                                    <div className="absolute inset-0 bg-black/35 flex items-center justify-center group-hover/vid:bg-black/10 transition-colors pointer-events-none">
                                      <div className="bg-white/20 backdrop-blur-md p-2 rounded-full border border-white/30 text-white shadow">
                                        <Play size={16} fill="white" />
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <img
                                    src={file.url}
                                    alt={file.filename}
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    loading="lazy"
                                  />
                                )}

                                {/* Asset Type Icon Badge */}
                                <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[9px] font-bold font-mono px-2 py-0.5 rounded-md flex items-center space-x-1 backdrop-blur-sm">
                                  {file.type === 'video' ? <Film size={10} /> : <FileImage size={10} />}
                                  <span>{file.type.toUpperCase()}</span>
                                </span>

                                {/* Size Badge */}
                                <span className="absolute top-2 right-2 bg-black/60 text-white text-[9px] font-bold font-mono px-2 py-0.5 rounded-md backdrop-blur-sm">
                                  {formattedSize}
                                </span>
                              </div>

                              {/* Details Area */}
                              <div className="p-4 flex-grow flex flex-col justify-between space-y-3">
                                <div className="space-y-1">
                                  <h3
                                    className="text-xs font-bold text-[#2C241E] truncate select-all cursor-pointer font-mono"
                                    title={file.filename}
                                  >
                                    {file.filename}
                                  </h3>
                                  <p className="text-[10px] text-[#2C241E]/40 font-mono">
                                    Uploaded: {formattedDate}
                                  </p>
                                </div>

                                {/* Usages list */}
                                <div className="space-y-1 pt-1 border-t border-[#2C241E]/5">
                                  <span className="text-[9px] uppercase font-bold text-[#2C241E]/40 font-mono tracking-wider">Usages:</span>
                                  {inUse ? (
                                    <div className="max-h-16 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                                      {file.usages.map((usage, idx) => (
                                        <span
                                          key={idx}
                                          className="bg-[#2D4A3E]/5 text-[#2D4A3E] border border-[#2D4A3E]/10 text-[9px] font-medium px-2 py-0.5 rounded-md block truncate hover:text-[#1E332A]"
                                          title={usage}
                                        >
                                          {usage}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[9px] font-medium px-2 py-0.5 rounded-md block truncate">
                                      ⚠️ Unused (Safe to delete)
                                    </span>
                                  )}
                                </div>

                                {/* Actions Area */}
                                <div className="border-t border-[#2C241E]/5 pt-3 flex justify-between items-center text-[10px] font-mono">
                                  <button
                                    type="button"
                                    onClick={() => handleCopyUrl(file.url)}
                                    className="flex items-center space-x-1 text-[#2D4A3E] hover:text-[#1E332A] px-2 py-1 rounded-lg hover:bg-[#2D4A3E]/5 border border-[#2D4A3E]/10 cursor-pointer font-medium transition-colors"
                                  >
                                    {copiedFileUrl === file.url ? (
                                      <>
                                        <Check size={11} className="text-emerald-600" />
                                        <span className="text-emerald-600 font-bold">Copied!</span>
                                      </>
                                    ) : (
                                      <>
                                        <Copy size={11} />
                                        <span>Copy URL</span>
                                      </>
                                    )}
                                  </button>

                                  <div className="flex items-center space-x-1">
                                    <a
                                      href={file.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="p-1 text-[#2C241E]/60 hover:text-[#2C241E] hover:bg-[#2C241E]/5 rounded transition-colors"
                                      title="Open asset in new tab"
                                    >
                                      <ExternalLink size={12} />
                                    </a>
                                    <button
                                      type="button"
                                      onClick={() => downloadFile(file.url, file.filename)}
                                      className="p-1 text-[#AF8D70] hover:text-[#8C5D3A] hover:bg-[#AF8D70]/5 rounded transition-colors"
                                      title="Download asset"
                                    >
                                      <Download size={12} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteUploadedFile(file.filename, file.usages.length)}
                                      className="flex items-center space-x-0.5 text-[#E25C5C] hover:text-[#c44343] p-1 rounded hover:bg-[#E25C5C]/5 cursor-pointer font-medium transition-colors"
                                      title="Delete file permanently"
                                    >
                                      <Trash2 size={12} />
                                      <span>Delete</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {filtered.length > visibleUploadsCount && (
                        <div className="flex justify-center pt-6 pb-2">
                          <button
                            type="button"
                            onClick={() => setVisibleUploadsCount(prev => prev + 12)}
                            className="flex items-center space-x-2 px-6 py-2.5 bg-white border border-[#2D4A3E]/15 hover:bg-[#2D4A3E]/5 text-[#2D4A3E] text-xs font-semibold rounded-xl transition-all cursor-pointer shadow-sm"
                          >
                            <RefreshCw size={12} className="animate-pulse" />
                            <span>Load More Assets ({filtered.length - visibleUploadsCount} remaining)</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {/* ======================= EVENT LOGS ======================= */}
          {activeTab === 'events' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex justify-between items-center border-b border-[#2D4A3E]/10 pb-4">
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-[#2C241E]">
                    Event Timeline logs
                  </h2>
                  <p className="text-xs text-[#2C241E]/60">
                    Maintain historical past memories or future upcoming family events.
                  </p>
                </div>
                <button
                  onClick={() => setIsEventModalOpen(true)}
                  className="flex items-center space-x-2 bg-[#2D4A3E] hover:bg-[#1E332A] text-white font-medium text-xs px-4 py-2 rounded-xl transition-all cursor-pointer"
                >
                  <Plus size={14} />
                  <span>Add Event Log</span>
                </button>
              </div>

              {/* Event admin listing */}
              <div className="space-y-4">
                {events.map((ev) => (
                  <div key={ev.id} className="bg-white border border-[#2D4A3E]/5 rounded-xl p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center shadow-sm gap-4">
                    <div className="flex items-center space-x-4">
                      <img src={ev.thumbnailUrl} className="w-14 h-14 object-cover rounded-xl border border-[#2D4A3E]/10" />
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${
                            ev.category === 'upcoming' 
                              ? 'bg-[#E2ECE9] text-[#2D4A3E]' 
                              : 'bg-[#F4DFD4] text-[#8C5D3A]'
                          }`}>
                            {ev.category}
                          </span>
                          <span className="text-xs font-semibold text-[#2C241E]/50">
                            {ev.type}
                          </span>
                        </div>
                        <h3 className="font-serif text-base font-semibold text-[#2C241E] mt-1">{ev.title}</h3>
                        <span className="text-xs text-[#2C241E]/40 font-medium block">Date: {ev.date} ({ev.dateRaw})</span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 self-end sm:self-center">
                      <button
                        onClick={() => startEditEvent(ev)}
                        className="p-2 border border-[#2D4A3E]/15 hover:bg-[#2D4A3E]/5 text-[#2D4A3E] rounded-full cursor-pointer transition-colors"
                        title="Edit Log"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(ev.id)}
                        className="p-2 border border-[#E25C5C]/20 hover:bg-[#E25C5C]/5 text-[#E25C5C] rounded-full cursor-pointer transition-colors"
                        title="Delete Log"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ======================= FARM FEATURES ======================= */}
          {activeTab === 'features' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex justify-between items-center border-b border-[#2D4A3E]/10 pb-4">
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-[#2C241E]">
                    Farm features
                  </h2>
                  <p className="text-xs text-[#2C241E]/60">
                    Add or update customized garden divisions or house features.
                  </p>
                </div>
                <button
                  onClick={() => setIsFeatureModalOpen(true)}
                  className="flex items-center space-x-2 bg-[#2D4A3E] hover:bg-[#1E332A] text-white font-medium text-xs px-4 py-2 rounded-xl transition-all cursor-pointer"
                >
                  <Plus size={14} />
                  <span>Add Feature</span>
                </button>
              </div>

              {/* Feature Cards list */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {features.map((feat) => (
                  <div key={feat.id} className="bg-white border border-[#2D4A3E]/10 rounded-2xl overflow-hidden flex flex-col justify-between">
                    <div>
                      <div className="relative h-32">
                        <img src={feat.imageUrl} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-4">
                        <h4 className="font-serif text-base font-semibold text-[#2C241E]">{feat.name}</h4>
                        <p className="text-xs text-[#2C241E]/70 line-clamp-4 mt-2 leading-relaxed">
                          {feat.description}
                        </p>
                      </div>
                    </div>

                    <div className="p-4 border-t border-[#2C241E]/5 flex justify-end space-x-2 bg-[#FAF7F2]/50">
                      <button
                        onClick={() => startEditFeature(feat)}
                        className="flex items-center space-x-1.5 px-3 py-1.5 border border-[#2D4A3E]/20 text-xs text-[#2D4A3E] font-medium hover:bg-[#2D4A3E]/5 rounded-lg transition-colors cursor-pointer"
                      >
                        <Edit size={12} />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDeleteFeature(feat.id)}
                        className="flex items-center space-x-1.5 px-3 py-1.5 border border-[#E25C5C]/20 text-xs text-[#E25C5C] font-medium hover:bg-[#E25C5C]/5 rounded-lg transition-colors cursor-pointer"
                      >
                        <Trash2 size={12} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ======================= SETTINGS ======================= */}
          {activeTab === 'settings' && (
            <div className="space-y-8 animate-fadeIn">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2C241E]">
                  System settings & Maintenance
                </h2>
                <p className="text-xs text-[#2C241E]/60">
                  Update database options and admin passcode requirements.
                </p>
              </div>



              {/* Passphrase block */}
              <form onSubmit={handleUpdatePassword} className="bg-white border border-[#2D4A3E]/10 p-6 rounded-2xl space-y-4 shadow-sm">
                <div className="flex items-center space-x-2 text-base font-semibold text-[#2C241E]">
                  <Key size={18} className="text-[#8C5D3A]" />
                  <span>Update Admin Passcode</span>
                </div>
                <p className="text-xs text-[#2C241E]/60">
                  Secure access to this dashboard with a new family passphrase.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 max-w-lg">
                  <input
                    type="password"
                    placeholder="Enter new private password"
                    value={newPass}
                    onChange={(e) => setNewPass(e.target.value)}
                    required
                    className="flex-grow bg-[#FAF7F2] border border-[#2D4A3E]/10 text-xs px-4 py-3 rounded-xl focus:border-[#2D4A3E] focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="px-5 py-3 bg-[#2D4A3E] hover:bg-[#1E332A] text-white text-xs font-semibold rounded-xl cursor-pointer transition-colors"
                  >
                    Save Passcode
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ======================= FEEDBACK REVIEW ======================= */}
          {activeTab === 'feedback' && (
            <div className="space-y-8 animate-fadeIn">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-[#2C241E]">
                    Guest Feedback Review
                  </h2>
                  <p className="text-xs text-[#2C241E]/60">
                    Review, approve, or reject feedback submitted by guests on the homepage.
                  </p>
                </div>
                <button
                  onClick={fetchAllFeedbacks}
                  disabled={loadingFeedbacks}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-[#E5E2D9] hover:bg-[#FAF9F6] text-xs font-semibold rounded-lg text-[#2D2D2A] transition-colors cursor-pointer"
                >
                  <RefreshCw size={14} className={loadingFeedbacks ? 'animate-spin' : ''} />
                  <span>Refresh</span>
                </button>
              </div>

              {loadingFeedbacks ? (
                <div className="flex justify-center items-center py-20 bg-white border border-[#E5E2D9] rounded-3xl">
                  <RefreshCw size={24} className="animate-spin text-[#2D4A3E]" />
                </div>
              ) : allFeedbacks.length === 0 ? (
                <div className="text-center py-20 bg-white border border-[#E5E2D9] rounded-3xl">
                  <MessageSquare size={32} className="mx-auto text-[#6B705C]/40 mb-4" />
                  <p className="handwritten text-lg text-[#6B705C]">No feedback submitted yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {allFeedbacks.map((f) => {
                    const handleStatusChange = async (action: 'approve' | 'reject' | 'delete') => {
                      try {
                        const url = action === 'delete' ? `/api/feedbacks/${f.id}` : `/api/feedbacks/${f.id}/${action}`;
                        const method = action === 'delete' ? 'DELETE' : 'POST';
                        const res = await fetch(url, { method });
                        if (res.ok) {
                          await fetchAllFeedbacks();
                          await onRefreshAllData();
                        }
                      } catch (err) {
                        console.error(`Failed to ${action} feedback:`, err);
                      }
                    };

                    return (
                      <div
                        key={f.id}
                        className="bg-white border border-[#E5E2D9] rounded-3xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 hover:shadow-md transition-shadow duration-300"
                      >
                        <div className="space-y-3 flex-grow">
                          <div className="flex items-center space-x-3">
                            <span className="font-serif text-base font-bold text-[#2C241E]">
                              {f.username}
                            </span>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                              f.status === 'approved'
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : f.status === 'rejected'
                                ? 'bg-red-50 text-red-700 border border-red-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}>
                              {f.status}
                            </span>
                          </div>
                          <p className="font-serif text-[15px] italic text-[#43423B] leading-relaxed">
                            "{f.feedback}"
                          </p>
                          <span className="block text-[10px] font-mono text-[#8E9288]">
                            Submitted: {new Date(f.createdAt).toLocaleString()}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2.5 shrink-0">
                          {f.status !== 'approved' && (
                            <button
                              onClick={() => handleStatusChange('approve')}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer font-mono"
                            >
                              Approve
                            </button>
                          )}
                          {f.status !== 'rejected' && (
                            <button
                              onClick={() => handleStatusChange('reject')}
                              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-wider rounded-lg transition-colors cursor-pointer font-mono"
                            >
                              Reject
                            </button>
                          )}
                          <button
                            onClick={() => handleStatusChange('delete')}
                            className="p-2 border border-[#E5E2D9] hover:bg-red-50 hover:border-red-200 text-[#6B705C] hover:text-red-600 rounded-lg transition-colors cursor-pointer"
                            title="Delete Permanently"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ======================= MODAL: ADD/EDIT MEMORY ======================= */}
      {isAlbumModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#FAF7F2] text-[#2C241E] w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl border border-[#2D4A3E]/15 p-6 animate-scaleUp">
            <div className="flex justify-between items-center border-b border-[#2C241E]/10 pb-3 mb-4">
              <span className="font-serif text-lg font-bold">
                {editingMemoryId ? 'Edit Memory' : 'Publish Memory Post'}
              </span>
              <button onClick={resetMemoryForm} className="p-1 hover:bg-[#2C241E]/5 rounded-full cursor-pointer"><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveMemory} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Memory Title (Required)</label>
                <input
                  type="text"
                  placeholder="e.g., Harvesting Tomatoes"
                  value={memoryTitle}
                  onChange={(e) => setMemoryTitle(e.target.value)}
                  required
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-[#2D4A3E]"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Date (YYYY-MM-DD)</label>
                <input
                  type="date"
                  value={memoryDate}
                  onChange={(e) => setMemoryDate(e.target.value)}
                  required
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-[#2D4A3E]"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Description (Optional)</label>
                <textarea
                  rows={2}
                  placeholder="Tell the story of this memory..."
                  value={memoryDesc}
                  onChange={(e) => setMemoryDesc(e.target.value)}
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs p-3 rounded-xl focus:outline-none"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Media URLs or Upload Files</label>
                  <label className="text-xs text-[#2D4A3E] hover:underline cursor-pointer flex items-center gap-1 font-semibold">
                    <Upload size={13} />
                    <span>Upload Photo(s) / 1 Video</span>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          const files = Array.from(e.target.files) as File[];
                          const videos = files.filter(f => f.type.startsWith('video/'));
                          
                          if (videos.length > 0) {
                            if (files.length > 1) {
                              alert('You can only upload a single video, and cannot mix video with photos.');
                              e.target.value = '';
                              return;
                            }
                          }
                          setMemoryPendingFiles(files);
                          setMemoryMediaUrl(''); // Clear text URL if they upload files
                        }
                      }}
                    />
                  </label>
                </div>
                <textarea
                  rows={2}
                  placeholder="Paste photo URL(s) separated by commas/newlines, or 1 video URL..."
                  value={memoryMediaUrl}
                  onChange={(e) => {
                    const text = e.target.value;
                    setMemoryMediaUrl(text);
                    if (text) {
                      setMemoryPendingFiles([]); // Clear pending files if they paste URLs
                    }
                  }}
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs p-3 rounded-xl focus:outline-none focus:border-[#2D4A3E] font-mono"
                />

                {/* Local or Remote Preview Area */}
                {((memoryMediaUrl.trim() && memoryMediaUrl.split(/[\n,]+/).map(u => u.trim()).filter(Boolean).length > 0) || memoryPendingFiles.length > 0) && (
                  <div className="space-y-2 bg-white border border-[#2D4A3E]/10 rounded-xl p-3">
                    <div className="flex justify-between items-center border-b border-[#2C241E]/5 pb-1">
                      <label className="text-[10px] font-bold text-[#2C241E]/40 uppercase tracking-wide">
                        Media Preview ({memoryPendingFiles.length > 0 ? memoryPendingFiles.length : memoryMediaUrl.split(/[\n,]+/).map(u => u.trim()).filter(Boolean).length} item(s))
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setMemoryMediaUrl('');
                          setMemoryPendingFiles([]);
                        }}
                        className="text-[10px] text-red-500 hover:underline font-semibold"
                      >
                        Clear All
                      </button>
                    </div>

                    {/* Featured Large Preview */}
                    <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center border border-[#2D4A3E]/10 shadow-inner">
                      {memoryPendingFiles.length > 0 ? (
                        memoryPendingFiles[0].type.startsWith('video/') ? (
                          <video src={URL.createObjectURL(memoryPendingFiles[0])} className="w-full h-full object-cover" controls />
                        ) : (
                          <img
                            src={URL.createObjectURL(memoryPendingFiles[0])}
                            className="w-full h-full object-cover animate-fadeIn"
                            style={{ filter: PHOTO_FILTERS.find(f => f.id === memoryFilter)?.style }}
                          />
                        )
                      ) : (
                        (() => {
                          const urls = memoryMediaUrl.split(/[\n,]+/).map(u => u.trim()).filter(Boolean);
                          if (urls[0]) {
                            return detectMediaType(urls[0]) === 'video' ? (
                              <video src={urls[0]} className="w-full h-full object-cover" controls />
                            ) : (
                              <img
                                src={urls[0]}
                                className="w-full h-full object-cover animate-fadeIn"
                                style={{ filter: PHOTO_FILTERS.find(f => f.id === memoryFilter)?.style }}
                              />
                            );
                          }
                          return null;
                        })()
                      )}
                      <span className="absolute bottom-2 left-2 text-[9px] bg-black/60 backdrop-blur-md text-white px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                        Featured Preview
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-2 max-h-36 overflow-y-auto p-1">
                      {memoryPendingFiles.length > 0 ? (
                        memoryPendingFiles.map((file, idx) => {
                          const isVid = file.type.startsWith('video/');
                          const localUrl = URL.createObjectURL(file);
                          return (
                            <div key={`local-${idx}`} className="relative aspect-square bg-black rounded-lg overflow-hidden flex items-center justify-center group border border-[#2D4A3E]/10">
                              {isVid ? (
                                <video src={localUrl} className="w-full h-full object-cover" controls />
                              ) : (
                                <img
                                  src={localUrl}
                                  className="w-full h-full object-cover animate-fadeIn"
                                  style={{ filter: PHOTO_FILTERS.find(f => f.id === memoryFilter)?.style }}
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setMemoryPendingFiles(prev => prev.filter((_, i) => i !== idx));
                                }}
                                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                              >
                                <X size={10} />
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        memoryMediaUrl.split(/[\n,]+/).map(u => u.trim()).filter(Boolean).map((url, idx) => {
                          const isVid = detectMediaType(url) === 'video';
                          return (
                            <div key={`url-${idx}`} className="relative aspect-square bg-black rounded-lg overflow-hidden flex items-center justify-center group border border-[#2D4A3E]/10">
                              {isVid ? (
                                <video src={url} className="w-full h-full object-cover" controls />
                              ) : (
                                <img
                                  src={url}
                                  className="w-full h-full object-cover animate-fadeIn"
                                  style={{ filter: PHOTO_FILTERS.find(f => f.id === memoryFilter)?.style }}
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const list = memoryMediaUrl.split(/[\n,]+/).map(u => u.trim()).filter(Boolean);
                                  list.splice(idx, 1);
                                  setMemoryMediaUrl(list.join(', '));
                                }}
                                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                              >
                                <X size={10} />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* Photo Filter Selector */}
                {memoryMediaType === 'image' && ((memoryMediaUrl.trim() && memoryMediaUrl.split(/[\n,]+/).map(u => u.trim()).filter(Boolean).length > 0) || memoryPendingFiles.length > 0) && (
                  <div className="space-y-2 border border-[#2D4A3E]/10 p-4 rounded-xl bg-white">
                    <label className="text-[10px] font-bold text-[#2C241E]/40 uppercase tracking-wide block">Select Photo Effect / Filter</label>
                    <div className="flex flex-wrap gap-1.5">
                      {PHOTO_FILTERS.map((filter) => (
                        <button
                          key={filter.id}
                          type="button"
                          onClick={() => setMemoryFilter(filter.id)}
                          className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg cursor-pointer transition-all border ${
                            memoryFilter === filter.id
                              ? 'bg-[#2D4A3E] text-white border-[#2D4A3E] shadow-xs'
                              : 'bg-[#FAF7F2] text-[#2C241E]/80 border-[#2D4A3E]/10 hover:bg-[#2D4A3E]/5'
                          }`}
                        >
                          {filter.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isUploading && (
                  <div className="text-[10px] text-[#2D4A3E] font-medium animate-pulse flex items-center gap-1">
                    <RefreshCw size={10} className="animate-spin" />
                    <span>Uploading media files, please wait...</span>
                  </div>
                )}
              </div>

              {/* Presets suggestions */}
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-[#2C241E]/40 uppercase tracking-wide block">Presets Suggestion (Click to use)</label>
                <div className="flex flex-wrap gap-2 pt-1 max-h-24 overflow-y-auto">
                  {PHOTO_PRESETS.map((preset, idx) => (
                    <button
                      key={`photo-${idx}`}
                      type="button"
                      onClick={() => {
                        setMemoryMediaUrl(preset.url);
                        setMemoryPendingFiles([]);
                      }}
                      className="text-[9px] px-2 py-1 rounded bg-[#2D4A3E]/5 border border-[#2D4A3E]/10 hover:bg-[#2D4A3E]/10 transition-colors text-left truncate max-w-[140px]"
                    >
                      📸 {preset.name}
                    </button>
                  ))}
                  {[
                    { name: 'Watering Plants Video', url: 'https://assets.mixkit.co/videos/preview/mixkit-watering-canned-plants-in-a-greenhouse-42354-large.mp4' },
                    { name: 'Tree Leaves Wind Video', url: 'https://assets.mixkit.co/videos/preview/mixkit-green-leaves-of-a-tree-in-the-wind-42352-large.mp4' },
                    { name: 'Sunlight Forest Video', url: 'https://assets.mixkit.co/videos/preview/mixkit-sunlight-shining-through-forest-trees-42512-large.mp4' }
                  ].map((preset, idx) => (
                    <button
                      key={`video-${idx}`}
                      type="button"
                      onClick={() => {
                        setMemoryMediaUrl(preset.url);
                        setMemoryPendingFiles([]);
                      }}
                      className="text-[9px] px-2 py-1 rounded bg-[#cb997e]/10 border border-[#cb997e]/30 hover:bg-[#cb997e]/20 transition-colors text-left truncate max-w-[140px] text-[#cb997e]"
                    >
                      🎥 {preset.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-[#2C241E]/10">
                <button
                  type="button"
                  onClick={resetMemoryForm}
                  className="px-4 py-2 hover:bg-[#2C241E]/5 text-xs font-medium rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#2D4A3E] hover:bg-[#1E332A] text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer"
                >
                  {editingMemoryId ? 'Save Memory' : 'Publish Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================= MODAL: ADD/EDIT EVENT ======================= */}
      {isEventModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#FAF7F2] text-[#2C241E] w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl border border-[#2D4A3E]/15 p-6 my-8 animate-scaleUp max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-[#2C241E]/10 pb-3 mb-4">
              <span className="font-serif text-lg font-bold">
                {editingEventId ? 'Edit Event Log' : 'Create New Event Log'}
              </span>
              <button onClick={resetEventForm} className="p-1 hover:bg-[#2C241E]/5 rounded-full cursor-pointer"><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveEvent} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Category</label>
                  <select
                    value={eventCategory}
                    onChange={(e) => setEventCategory(e.target.value as any)}
                    className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none animate-fadeIn"
                  >
                    <option value="upcoming">Upcoming Gathering</option>
                    <option value="past">Past Memory / Log</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Log tag (e.g. Dinner, Family)</label>
                  <input
                    type="text"
                    placeholder="e.g., Dinner, Maintenance"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                    required
                    className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Event Title</label>
                <input
                  type="text"
                  placeholder="e.g., Thanksgiving Day Roast"
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  required
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Date Name (User friendly)</label>
                  <input
                    type="text"
                    placeholder="e.g., Oct 14 or June 2024"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    required
                    className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Calendar Date (Sorting)</label>
                  <input
                    type="date"
                    value={eventDateRaw}
                    onChange={(e) => setEventDateRaw(e.target.value)}
                    className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Short Summary Description</label>
                <input
                  type="text"
                  placeholder="Brief 1-2 sentence description..."
                  value={eventDesc}
                  onChange={(e) => setEventDesc(e.target.value)}
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none"
                />
              </div>



              {/* Event Images (URLs or upload files) */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Event Images (URLs or select local photos)</label>
                  <label className="text-xs text-[#2D4A3E] hover:underline cursor-pointer flex items-center gap-1 font-semibold">
                    <Upload size={13} />
                    <span>Select Local Photos</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) {
                          const filesArray = Array.from(e.target.files);
                          setEventPendingFiles(prev => [...prev, ...filesArray]);
                        }
                      }}
                    />
                  </label>
                </div>
                <textarea
                  rows={3}
                  placeholder="Paste image URLs separated by commas or new lines, or select local photos..."
                  value={eventGallery}
                  onChange={(e) => setEventGallery(e.target.value)}
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs p-3 rounded-xl focus:outline-none font-mono"
                />

                {/* Event Local & URL Preview Area */}
                {(eventGallery.trim() || eventPendingFiles.length > 0) && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-[#2C241E]/40 uppercase tracking-wide block">Event Images Preview ({eventPendingFiles.length + eventGallery.split(/[\n,]+/).map(u => u.trim()).filter(Boolean).length} images - first is cover)</label>
                    
                    {/* Featured Large Cover Preview */}
                    <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center border border-[#2D4A3E]/10 shadow-inner">
                      {(() => {
                        const urls = eventGallery.split(/[\n,]+/).map(u => u.trim()).filter(Boolean);
                        if (urls[0]) {
                          return (
                            <img
                              src={urls[0]}
                              className="w-full h-full object-cover animate-fadeIn"
                              style={{ filter: PHOTO_FILTERS.find(f => f.id === eventFilter)?.style }}
                            />
                          );
                        } else if (eventPendingFiles.length > 0) {
                          return (
                            <img
                              src={URL.createObjectURL(eventPendingFiles[0])}
                              className="w-full h-full object-cover animate-fadeIn"
                              style={{ filter: PHOTO_FILTERS.find(f => f.id === eventFilter)?.style }}
                            />
                          );
                        }
                        return null;
                      })()}
                      <span className="absolute bottom-2 left-2 text-[9px] bg-black/60 backdrop-blur-md text-white px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                        Cover Image Preview
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-2 max-h-36 overflow-y-auto p-2 border border-[#2D4A3E]/10 rounded-xl bg-white">
                      {/* URL Previews */}
                      {eventGallery.split(/[\n,]+/).map(u => u.trim()).filter(Boolean).map((url, idx) => (
                        <div key={`url-${idx}`} className="relative group aspect-square bg-[#FAF7F2] border border-[#2D4A3E]/5 rounded-lg overflow-hidden">
                          <img
                            src={url}
                            className="w-full h-full object-cover animate-fadeIn"
                            style={{ filter: PHOTO_FILTERS.find(f => f.id === eventFilter)?.style }}
                          />
                          {idx === 0 && (
                            <span className="absolute top-1 left-1 bg-[#2D4A3E] text-white text-[8px] font-bold px-1.5 py-0.5 rounded shadow">Cover</span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const lines = eventGallery.split(/[\n,]+/).map(u => u.trim()).filter(Boolean);
                              lines.splice(idx, 1);
                              setEventGallery(lines.join(', '));
                            }}
                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                      {/* Pending local files previews */}
                      {eventPendingFiles.map((file, idx) => {
                        const localUrl = URL.createObjectURL(file);
                        const isFirstAndNoUrls = idx === 0 && eventGallery.split(/[\n,]+/).map(u => u.trim()).filter(Boolean).length === 0;
                        return (
                          <div key={`local-${idx}`} className="relative group aspect-square bg-[#FAF7F2] border border-[#2D4A3E]/5 rounded-lg overflow-hidden">
                            <img
                              src={localUrl}
                              className="w-full h-full object-cover animate-fadeIn"
                              style={{ filter: PHOTO_FILTERS.find(f => f.id === eventFilter)?.style }}
                            />
                            <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-[8px] text-white px-1 py-0.5 truncate">{file.name}</span>
                            {isFirstAndNoUrls && (
                              <span className="absolute top-1 left-1 bg-[#2D4A3E] text-white text-[8px] font-bold px-1.5 py-0.5 rounded shadow">Cover</span>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setEventPendingFiles(prev => prev.filter((_, i) => i !== idx));
                              }}
                              className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                              <X size={10} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Event Photo Filter Selector */}
                {(eventGallery.trim() || eventPendingFiles.length > 0) && (
                  <div className="space-y-2 border border-[#2D4A3E]/10 p-4 rounded-xl bg-white">
                    <label className="text-[10px] font-bold text-[#2C241E]/40 uppercase tracking-wide block">Select Photo Effect / Filter</label>
                    <div className="flex flex-wrap gap-1.5">
                      {PHOTO_FILTERS.map((filter) => (
                        <button
                          key={filter.id}
                          type="button"
                          onClick={() => setEventFilter(filter.id)}
                          className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg cursor-pointer transition-all border ${
                            eventFilter === filter.id
                              ? 'bg-[#2D4A3E] text-white border-[#2D4A3E] shadow-xs'
                              : 'bg-[#FAF7F2] text-[#2C241E]/80 border-[#2D4A3E]/10 hover:bg-[#2D4A3E]/5'
                          }`}
                        >
                          {filter.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isUploading && (
                  <div className="text-[10px] text-[#2D4A3E] font-medium animate-pulse flex items-center gap-1">
                    <RefreshCw size={10} className="animate-spin" />
                    <span>Uploading photos, please wait...</span>
                  </div>
                )}
              </div>

              {/* Unsplash Auto Fill option */}
              <button
                type="button"
                onClick={() => {
                  setEventGallery('https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&q=80&w=800, https://images.unsplash.com/photo-1505236272433-0611709d4385?auto=format&fit=crop&q=80&w=800, https://images.unsplash.com/photo-1515003197210-e0cd71810b5f?auto=format&fit=crop&q=80&w=800');
                  triggerNoti('Seeded banquet dinner images into event form.');
                }}
                className="text-[10px] text-[#2D4A3E] font-semibold bg-[#2D4A3E]/5 px-3 py-1.5 rounded hover:bg-[#2D4A3E]/10 inline-block"
              >
                Autofill sample dinner gallery images
              </button>

              <div className="flex justify-end space-x-2 pt-4 border-t border-[#2C241E]/10">
                <button
                  type="button"
                  onClick={resetEventForm}
                  className="px-4 py-2 hover:bg-[#2C241E]/5 text-xs font-medium rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#2D4A3E] hover:bg-[#1E332A] text-white text-xs font-semibold rounded-xl cursor-pointer"
                >
                  {editingEventId ? 'Update Log' : 'Publish Log'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================= MODAL: ADD/EDIT FEATURE ======================= */}
      {isFeatureModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#FAF7F2] text-[#2C241E] w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl border border-[#2D4A3E]/15 p-6 animate-scaleUp">
            <div className="flex justify-between items-center border-b border-[#2C241E]/10 pb-3 mb-4">
              <span className="font-serif text-lg font-bold">
                {editingFeatureId ? 'Edit Feature Area' : 'Add Framed Area / Feature'}
              </span>
              <button onClick={resetFeatureForm} className="p-1 hover:bg-[#2C241E]/5 rounded-full cursor-pointer"><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveFeature} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Feature Name</label>
                <input
                  type="text"
                  placeholder="e.g., Apple Orchard & Greenhouse"
                  value={featureName}
                  onChange={(e) => setFeatureName(e.target.value)}
                  required
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Description / Details</label>
                <textarea
                  rows={3}
                  placeholder="What makes this area special? Space for cozy summaries."
                  value={featureDesc}
                  onChange={(e) => setFeatureDesc(e.target.value)}
                  required
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs p-3 rounded-xl focus:outline-none"
                />
              </div>

              {/* Feature Image (URL or upload files) */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold text-[#2C241E]/60 uppercase">Feature Image (URL or select local photo)</label>
                  <label className="text-xs text-[#2D4A3E] hover:underline cursor-pointer flex items-center gap-1 font-semibold">
                    <Upload size={13} />
                    <span>Select Local Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) {
                          const filesArray = Array.from(e.target.files);
                          setFeaturePendingFiles(filesArray);
                        }
                      }}
                    />
                  </label>
                </div>
                <input
                  type="text"
                  placeholder="Paste direct Unsplash/image link or select local photo..."
                  value={featureUrl}
                  onChange={(e) => setFeatureUrl(e.target.value)}
                  className="w-full bg-white border border-[#2D4A3E]/10 text-xs px-3 py-2.5 rounded-xl focus:outline-none"
                />

                {/* Feature Local & URL Preview Area */}
                {(featureUrl.trim() || featurePendingFiles.length > 0) && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-[#2C241E]/40 uppercase tracking-wide block">Feature Image Preview</label>
                    <div className="grid grid-cols-4 gap-2 max-h-36 overflow-y-auto p-2 border border-[#2D4A3E]/10 rounded-xl bg-white">
                      {/* URL Preview */}
                      {featureUrl.trim() && (
                        <div className="relative group aspect-square bg-[#FAF7F2] border border-[#2D4A3E]/5 rounded-lg overflow-hidden">
                          <img
                            src={featureUrl}
                            className="w-full h-full object-cover animate-fadeIn"
                            style={{ filter: PHOTO_FILTERS.find(f => f.id === featureFilter)?.style }}
                          />
                          <button
                            type="button"
                            onClick={() => setFeatureUrl('')}
                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      )}
                      {/* Pending local file preview */}
                      {featurePendingFiles.map((file, idx) => {
                        const localUrl = URL.createObjectURL(file);
                        return (
                          <div key={`local-feat-${idx}`} className="relative group aspect-square bg-[#FAF7F2] border border-[#2D4A3E]/5 rounded-lg overflow-hidden">
                            <img
                              src={localUrl}
                              className="w-full h-full object-cover animate-fadeIn"
                              style={{ filter: PHOTO_FILTERS.find(f => f.id === featureFilter)?.style }}
                            />
                            <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-[8px] text-white px-1 py-0.5 truncate">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFeaturePendingFiles([]);
                              }}
                              className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                              <X size={10} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Feature Photo Filter Selector */}
                {(featureUrl.trim() || featurePendingFiles.length > 0) && (
                  <div className="space-y-2 border border-[#2D4A3E]/10 p-4 rounded-xl bg-white">
                    <label className="text-[10px] font-bold text-[#2C241E]/40 uppercase tracking-wide block">Select Photo Effect / Filter</label>
                    <div className="flex flex-wrap gap-1.5">
                      {PHOTO_FILTERS.map((filter) => (
                        <button
                          key={filter.id}
                          type="button"
                          onClick={() => setFeatureFilter(filter.id)}
                          className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg cursor-pointer transition-all border ${
                            featureFilter === filter.id
                              ? 'bg-[#2D4A3E] text-white border-[#2D4A3E] shadow-xs'
                              : 'bg-[#FAF7F2] text-[#2C241E]/80 border-[#2D4A3E]/10 hover:bg-[#2D4A3E]/5'
                          }`}
                        >
                          {filter.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Preset autofills */}
              <button
                type="button"
                onClick={() => {
                  setFeatureUrl('https://images.unsplash.com/photo-1621360841013-c7683c659ec6?auto=format&fit=crop&q=80&w=800');
                  setFeatureName('The Ancient Stone Well');
                  triggerNoti('Seeded Stone Well specs into feature form.');
                }}
                className="text-[10px] text-[#8C5D3A] font-semibold bg-[#8C5D3A]/5 px-3 py-1.5 rounded hover:bg-[#8C5D3A]/10 inline-block"
              >
                Autofill Stone Well template
              </button>

              <div className="flex justify-end space-x-2 pt-4 border-t border-[#2C241E]/10">
                <button
                  type="button"
                  onClick={resetFeatureForm}
                  className="px-4 py-2 hover:bg-[#2C241E]/5 text-xs font-medium rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#2D4A3E] hover:bg-[#1E332A] text-white text-xs font-semibold rounded-xl cursor-pointer"
                >
                  {editingFeatureId ? 'Update Feature' : 'Create Feature'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
